import { useEffect, useState } from 'react'

import { useAppSelector } from 'redux/hooks'
import { useCustomCardAsCardType } from 'hooks/customCard'

import SaveService from 'services/save.service'
import CustomCardService from 'services/customCards.service'
import ToastService from 'services/toast.service'

import { CustomCard } from 'services/apiTypes/customCards.types'
import { CardType } from 'types/deck'

import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'
import LineLoader from 'components/elements/LineLoader'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import CustomCardImageCard from 'components/customCards/CustomCardImageCard'
import ImageCard from 'components/card/deckCards/imageCard'

import styles from './customCardListOverlay.module.scss'
import Link from 'components/elements/Link'

type Props = {
  open: boolean
  onClose: () => void
}

const CustomCardListOverlay = ({ open, onClose }: Props) => {
  const [loading, setLoading] = useState(true)
  const [customCards, setCustomCards] = useState(new Array<CustomCard>())
  const [textFilter, setTextFilter] = useState('')
  const [nextUrl, setNextUrl] = useState<string | null>(null)

  const cardMap = useAppSelector(state => state.deck.cardMap)
  const cards = useCustomCardAsCardType(customCards)

  const handleUpdateCustomCard = (updatedCard: CustomCard) => {
    const cardIndex = customCards.findIndex(customCard => customCard.id === updatedCard.id)

    if (cardIndex < 0) return // Wut

    const updatedCustomCards = [...customCards]

    updatedCustomCards[cardIndex] = updatedCard

    setCustomCards(updatedCustomCards)
  }

  const handleAddCardToDeckState = (card: CardType) => {
    SaveService.save({ ...card, qty: 1 }).catch(() =>
      ToastService.create('Unabled to add card to deck', 'Save service', 'error'),
    )
  }

  useEffect(() => {
    if (!open) {
      setLoading(true)
      setCustomCards([])

      return
    }

    handleFetchCustomCards()
  }, [open])

  const handleFetchCustomCards = (searchParams: Record<string, any> = {}, nextUrl?: string) => {
    setLoading(true)

    return CustomCardService.list(searchParams, nextUrl)
      .then(res => {
        // IF it was a next page request, append the results to the end rather than clearing them out
        if (nextUrl) setCustomCards([...customCards, ...res.results])
        else setCustomCards(res.results)

        setNextUrl(res.next)
      })
      .catch(err => ToastService.create(err, 'Custom Card Service', 'error'))
      .finally(() => setLoading(false))
  }

  const handleFilterCustomCards = (e: React.FormEvent) => {
    e.preventDefault()

    handleFetchCustomCards({ search: textFilter })
  }

  const customCardIdsToDeckInternalId = Object.values(cardMap).reduce(
    (acc, card) => {
      if (!card.customCard?.id) return acc

      return { ...acc, [card.customCard.id]: card }
    },
    {} as Record<number, CardType>,
  )

  const displayCards = cards.map(card => {
    const customCardId = card.customCard?.id
    if (!customCardId) return card

    if (customCardIdsToDeckInternalId[customCardId]) return customCardIdsToDeckInternalId[customCardId]

    return card
  })

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <h2>Custom cards</h2>

      <div className={styles.controls}>
        <form onSubmit={handleFilterCustomCards} className={styles.filterForm}>
          <PhatInput
            header="Full text search"
            placeholder="Card name or text box"
            value={textFilter}
            onChange={setTextFilter}
          />
          <PhatButton icon="search" color="green" type="submit" />
        </form>

        <Link href="/custom-cards">
          <PhatButton color="blue" icon="add circle">
            Create custom card
          </PhatButton>
        </Link>
      </div>

      {loading && <LineLoader />}

      {!loading && !customCards.length && <div>No custom cards found</div>}

      <div className={styles.cards}>
        {displayCards.map(card => {
          if (!card.qty)
            return (
              <CustomCardImageCard
                usedInOverlay
                key={card.id}
                card={card}
                onCardEdited={handleUpdateCustomCard}
                onAddToDeckState={handleAddCardToDeckState}
                noExtrasMenu
              />
            )

          // Replace with a card that is actually in the deck if the card is already in the deck
          return <ImageCard cardId={card.id} hidePrices usedInOverlay />
        })}
      </div>

      {nextUrl && <PhatButton onClick={() => handleFetchCustomCards({}, nextUrl)}>Load more</PhatButton>}
      {nextUrl && loading && <LineLoader />}
    </GlobalOverlayStack>
  )
}

export default CustomCardListOverlay
